import * as React from 'react';
import { Alert, Button, Spinner } from '@amzn/awsui-components-react';
import { isChunkLoadError, recoverFromChunkError } from 'utils/chunkLoadRecovery';
import { recordRumEvent, RumEventType } from 'utils/cloudWatchRUM';


interface State {
  error: string;
  // A chunk-load error was caught but recovery did NOT trigger a reload (reload
  // cap exhausted), so a reload is not coming -- show an explicit refresh prompt
  // instead of a permanent spinner.
  chunkReloadExhausted: boolean;
}

export class ErrorBoundary extends React.PureComponent<unknown, State> {
  constructor(props: State) {
    super(props);
    this.state = { error: '', chunkReloadExhausted: false };
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  static getDerivedStateFromError(error: Error): object {
    return { error: error.message };
  }

  // A failed React.lazy import surfaces here (not as a window error/rejection
  // event), where it would otherwise render a permanent spinner -- the
  // "infinite loading" reported for the app. If the caught error is a webpack
  // chunk-load failure, trigger a guarded reload to re-fetch current chunk
  // hashes. The guard (see chunkLoadRecovery) reloads at most a bounded number of
  // times, then returns false so we surface a refresh prompt instead of looping.
  componentDidCatch(error: Error): void {
    if (isChunkLoadError(error)) {
      const reloading = recoverFromChunkError(error);
      if (!reloading) {
        // Cap hit: no reload is coming, so replace the bare spinner with a
        // prompt the user can act on.
        this.setState({ chunkReloadExhausted: true });
      }
      return;
    }
    // Non-chunk render error: report it so it isn't silently swallowed.
    recordRumEvent(RumEventType.ErrorBoundaryCaught, { message: error?.message || 'unknown' });
  }

  render(): any {
    const { children } = this.props;
    const { error, chunkReloadExhausted } = this.state;
    if (error) {
      if (chunkReloadExhausted) {
        return (
          <Alert
            type="error"
            header="Couldn't load the page"
            action={<Button onClick={() => window.location.reload()}>Refresh</Button>}
          >
            Part of the app failed to load. Please refresh the page.
          </Alert>
        );
      }
      return (
        <Spinner size="large" />
      );
    }

    return children;
  }
}
